import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import DashboardLayout from './DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import {
  useGetAccountBehaviourTrackingsQuery,
  useGetBehaviourEmployeesQuery,
} from '../../redux/behaviourApi';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { SearchableSelect } from './SearchableSelect';
import TablePagination from './TablePagination';
import { FilterToggleButton } from './FilterToggleButton';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import {
  computeTrackingComplianceSummary,
  extractPerformance,
  extractTrackingList,
  formatComplianceStatus,
  formatPercent,
  getNonComplianceCount,
  performanceFromTrackingRow,
  trackingAccountIdFromRow,
} from '../../utils/behaviourTracking';
import BehaviourPerformanceModal from './BehaviourPerformanceModal';
import {
  behaviourListPath,
  behaviourListStateToSearchParams,
  getInitialBehaviourListState,
} from '../../utils/behaviourListState';

function firstText(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    if (typeof value === 'object') {
      const nested = value.name ?? value.title ?? value.label ?? '';
      if (nested) return String(nested);
      continue;
    }
    return String(value);
  }
  return '';
}

function extractBehaviours(row) {
  if (!row || typeof row !== 'object') return [];
  if (Array.isArray(row.behaviours)) return row.behaviours;
  if (Array.isArray(row.behaviour_details)) return row.behaviour_details;
  if (Array.isArray(row.account_behaviour_trackings)) return row.account_behaviour_trackings;
  if (Array.isArray(row.details)) return row.details;
  return [];
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

function formatReviewMonth(value, trackingDate) {
  if (value) return String(value);
  if (!trackingDate) return '—';
  const d = new Date(trackingDate);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

function mapTrackingRow(row, index) {
  const account = row?.account || row?.employee || {};
  const trackingDate = firstText(row?.tracking_date, row?.date, row?.created_at);
  const employeeCode = firstText(
    row?.employee_id,
    row?.emp_id,
    row?.employee_code,
    account?.employee_id,
    account?.emp_id
  );
  const behaviours = extractBehaviours(row);
  const summary = computeTrackingComplianceSummary(behaviours);
  return {
    __key: row?.id ?? row?.tracking_id ?? `row-${index}`,
    trackingId: row?.id ?? row?.tracking_id ?? null,
    accountId: trackingAccountIdFromRow(row),
    employeeId: employeeCode,
    employeeName: firstText(
      row?.employee_name,
      row?.account_name,
      row?.name,
      account?.name
    ),
    employeeCode,
    designation: firstText(
      row?.designation,
      row?.designation_name,
      account?.designation_name,
      account?.designation
    ),
    department: firstText(
      row?.accountable_department_name,
      row?.department_name,
      row?.department,
      account?.department_name,
      account?.accountable_department_name
    ),
    trackingDate,
    trackingDateLabel: formatDate(trackingDate),
    reportingManager: firstText(
      row?.reporting_supervisor_name,
      row?.reporting_manager_name,
      row?.reporting_manager
    ),
    reviewedBy: firstText(row?.reviewed_by_name, row?.reviewed_by),
    reviewMonth: formatReviewMonth(
      firstText(row?.review_month, row?.month),
      trackingDate
    ),
    behaviours,
    monthlyPerformance: firstText(
      row?.monthly_performance,
      row?.monthlyPerformance,
      summary.complianceRate
    ),
    yearlyPerformance: firstText(row?.yearly_performance, row?.yearlyPerformance),
    complianceSummary: summary,
    raw: row,
  };
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
  };
}

function rowMatchesEmployee(row, employeeKey, options = []) {
  if (!employeeKey) return true;
  const selected = String(employeeKey);
  const opt = options.find((o) => String(o.value) === selected);
  const keys = new Set(
    [selected, opt?.value, opt?.code, opt?.accountId]
      .filter((v) => v != null && String(v).trim() !== '')
      .map((v) => String(v))
  );
  return keys.has(String(row?.employeeId || '')) || keys.has(String(row?.employeeCode || ''));
}

function AdminBehaviourTracking() {
  const { can, canAction, canBehaviourTrack } = useAuth();
  const isManagerView = Boolean(canBehaviourTrack);
  const canViewPage = can('behaviour_tracking') || isManagerView;
  const canViewBehaviour = canAction('behaviour_tracking', 'view') || isManagerView;
  const canEditBehaviour = canAction('behaviour_tracking', 'edit') || isManagerView;
  const hasRowActions = canViewBehaviour || canEditBehaviour;
  const defaultRange = useMemo(() => currentMonthRange(), []);
  const defaultDates = useMemo(
    () => ({ from: defaultRange.start, to: defaultRange.end }),
    [defaultRange.start, defaultRange.end]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialListState] = useState(() => getInitialBehaviourListState(defaultDates));
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState(initialListState.dateFilter);
  const [dateFilter, setDateFilter] = useState(initialListState.dateFilter);
  const [employeeDraft, setEmployeeDraft] = useState(initialListState.employee);
  const [employeeFilter, setEmployeeFilter] = useState(initialListState.employee);
  const [viewRow, setViewRow] = useState(null);
  const [performanceTarget, setPerformanceTarget] = useState(null);
  const [isOverallPerformanceOpen, setIsOverallPerformanceOpen] = useState(false);
  const pageSize = DEFAULT_PAGE_SIZE;

  const { data: behaviourEmployeesRes } = useGetBehaviourEmployeesQuery(undefined, {
    skip: !canViewPage,
  });
  const employeeOptions = useMemo(() => {
    const list =
      behaviourEmployeesRes?.data ??
      behaviourEmployeesRes?.employees ??
      behaviourEmployeesRes ??
      [];
    if (!Array.isArray(list)) return [];
    return list
      .map((emp) => {
        const code = String(emp?.employee_code || emp?.emp_id || '').trim();
        const accountId = emp?.employee_id ?? emp?.account_id ?? emp?.id;
        const value = code || (accountId != null ? String(accountId) : '');
        if (!value) return null;
        const name = String(emp?.name || '').trim();
        const label = name && code ? `${name} (${code})` : name || code || String(accountId);
        return { value, label, code, accountId: accountId != null ? String(accountId) : '' };
      })
      .filter(Boolean);
  }, [behaviourEmployeesRes]);

  const selectedEmployee = useMemo(
    () => employeeOptions.find((o) => String(o.value) === String(employeeFilter || '')) || null,
    [employeeOptions, employeeFilter]
  );
  const selectedAccountId = String(selectedEmployee?.accountId || '').trim();

  const resolveRowAccountId = (row) => {
    const fromRow = trackingAccountIdFromRow(row?.raw) ?? trackingAccountIdFromRow(row);
    if (fromRow) return String(fromRow);
    return '';
  };

  const editTrackingPath = (row) => {
    const accountId = resolveRowAccountId(row);
    const trackingId = encodeURIComponent(String(row.trackingId));
    const qs = accountId ? `?account_id=${encodeURIComponent(accountId)}` : '';
    return `/dashboard/admin/behaviour-tracking/${trackingId}/edit${qs}`;
  };

  const editTrackingState = (row) => {
    const accountId = resolveRowAccountId(row);
    return {
      tracking: {
        ...(row.raw && typeof row.raw === 'object' ? row.raw : {}),
        id: row.trackingId,
        tracking_id: row.trackingId,
        tracking_date: row.raw?.tracking_date ?? row.raw?.date ?? row.trackingDate,
        ...(accountId ? { account_id: Number(accountId) } : {}),
        employee_code: row.employeeCode || row.employeeId || '',
        employee_name: row.employeeName || '',
      },
      accountId: accountId || null,
      employeeCode: row.employeeCode || row.employeeId || '',
      from: behaviourListPath({ employee: employeeFilter, dateFilter }),
    };
  };

  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (selectedAccountId) params.accountId = selectedAccountId;
    if (dateFilter.from) params.start_date = dateFilter.from;
    if (dateFilter.to) params.end_date = dateFilter.to;
    return params;
  }, [page, pageSize, search, selectedAccountId, dateFilter.from, dateFilter.to]);

  const { data, isLoading, isFetching, error, refetch } =
    useGetAccountBehaviourTrackingsQuery(listQuery, {
      skip: !canViewPage,
    });

  const performanceQuery = useMemo(() => {
    const accountId = String(performanceTarget?.accountId || '').trim();
    if (!accountId) return null;
    const params = { accountId, page: 1, per_page: pageSize };
    if (dateFilter.from) params.start_date = dateFilter.from;
    if (dateFilter.to) params.end_date = dateFilter.to;
    return params;
  }, [performanceTarget?.accountId, pageSize, dateFilter.from, dateFilter.to]);

  const { data: performanceRes, isLoading: isPerformanceLoading, isFetching: isPerformanceFetching } =
    useGetAccountBehaviourTrackingsQuery(performanceQuery, {
      skip: !canViewPage || !performanceQuery,
    });

  const rows = useMemo(
    () => extractTrackingList(data).map((row, index) => mapTrackingRow(row, index)),
    [data]
  );
  const listPerformance = useMemo(() => extractPerformance(data), [data]);
  const rowPerformance = useMemo(() => {
    const fromApi = extractPerformance(performanceRes);
    if (fromApi.behaviours.length > 0 || fromApi.overallPercentage != null) return fromApi;
    if (
      selectedAccountId &&
      selectedAccountId === String(performanceTarget?.accountId || '')
    ) {
      if (listPerformance.behaviours.length > 0 || listPerformance.overallPercentage != null) {
        return listPerformance;
      }
    }
    return performanceFromTrackingRow(performanceTarget?.row);
  }, [performanceRes, performanceTarget, selectedAccountId, listPerformance]);

  const { totalCount, pageSize: effectivePageSize } = getServerPagination(data, {
    page,
    pageSize,
  });
  const currentPage = page;
  const pageItems = useMemo(() => {
    if (selectedAccountId || !employeeFilter) return rows;
    return rows.filter((row) => rowMatchesEmployee(row, employeeFilter, employeeOptions));
  }, [rows, selectedAccountId, employeeFilter, employeeOptions]);

  const hasActiveFilters = Boolean(
    employeeFilter ||
      dateFilter.from !== defaultDates.from ||
      dateFilter.to !== defaultDates.to
  );

  useEffect(() => {
    const next = behaviourListStateToSearchParams({
      employee: employeeFilter,
      dateFilter,
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [employeeFilter, dateFilter, searchParams, setSearchParams]);

  const managerColSpan = 8 + (hasRowActions ? 1 : 0);
  const employeeColSpan = 4 + (hasRowActions ? 1 : 0);
  const colSpan = isManagerView ? managerColSpan : employeeColSpan;

  const isPending = isLoading || isFetching;
  const showEmpty = !error && !isPending && pageItems.length === 0;
  const showData = !error && pageItems.length > 0;

  const openPerformanceForSelectedEmployee = () => {
    if (!selectedAccountId) return;
    const nameFromLabel = selectedEmployee?.label
      ? selectedEmployee.label.replace(/\s*\([^)]*\)\s*$/, '').trim()
      : '';
    setPerformanceTarget({
      accountId: selectedAccountId,
      name: nameFromLabel,
      code: selectedEmployee?.code || '',
      row: pageItems[0] || null,
    });
  };

  if (!canViewPage) {
    return (
      <Navigate
        to={can('employees') ? '/dashboard/admin/employees' : '/landing'}
        replace
      />
    );
  }

  return (
    <DashboardLayout title="Behaviour Tracking">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full min-w-0 md:max-w-xs">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search Behaviour Tracking"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <FilterToggleButton
                  active={hasActiveFilters}
                  onClick={() => setIsFilterOpen((prev) => !prev)}
                  ariaLabel="Toggle date filter"
                />
                {!isManagerView ? (
                  <button
                    type="button"
                    onClick={() => setIsOverallPerformanceOpen(true)}
                    className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
                  >
                    Performance
                  </button>
                ) : null}
              </div>
            </div>

            {isFilterOpen ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <label className="flex flex-col text-sm text-gray-700">
                  Employees
                  <SearchableSelect
                    name="behaviourEmployee"
                    value={employeeDraft}
                    onChange={(e) => setEmployeeDraft(e.target.value || '')}
                    options={employeeOptions}
                    placeholder="All Employees"
                    clearable
                    className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col text-sm text-gray-700">
                  From
                  <input
                    type="date"
                    value={dateDraft.from}
                    onChange={(e) => {
                      setDateDraft((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }));
                    }}
                    className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <label className="flex flex-col text-sm text-gray-700">
                  To
                  <input
                    type="date"
                    value={dateDraft.to}
                    onChange={(e) => {
                      setDateDraft((prev) => ({
                        ...prev,
                        to: e.target.value,
                      }));
                    }}
                    className="mt-1 rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                <div className="flex items-end gap-2 sm:col-span-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDateFilter(dateDraft);
                      setEmployeeFilter(employeeDraft);
                      setPage(1);
                    }}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm"
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const nextDates = { from: defaultRange.start, to: defaultRange.end };
                      setDateDraft(nextDates);
                      setDateFilter(nextDates);
                      setEmployeeDraft('');
                      setEmployeeFilter('');
                      setPage(1);
                      setIsFilterOpen(false);
                    }}
                    className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            ) : null}

            {isManagerView && employeeFilter && selectedAccountId ? (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={openPerformanceForSelectedEmployee}
                  className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
                >
                  Performance
                </button>
              </div>
            ) : null}
          </div>

          {showEmpty ? (
            <p className="py-8 text-center text-sm text-gray-500">No behaviour data found.</p>
          ) : null}

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={pageItems.length > 0}
            error={error}
            refetch={refetch}
            message="Loading behaviour data…"
            className="py-6 md:hidden"
          />

          {showData ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {pageItems.map((row) => (
                <li
                  key={row.__key}
                  className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 space-y-1">
                      {isManagerView ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">
                            {row.employeeName || '—'}
                            {row.employeeCode ? (
                              <span className="ml-1 text-xs font-normal text-gray-500">
                                ({row.employeeCode})
                              </span>
                            ) : null}
                          </p>
                          <p className="text-xs text-gray-600">
                            {row.designation || '—'} · {row.department || '—'}
                          </p>
                        </>
                      ) : null}
                      <p className="text-xs text-gray-600">
                        <span className="text-gray-500">Tracking Date:</span>{' '}
                        <span className="font-medium text-gray-800">
                          {row.trackingDateLabel}
                        </span>
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="text-gray-500">Review Month:</span>{' '}
                        <span className="font-medium text-gray-800">{row.reviewMonth}</span>
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="text-gray-500">Reporting Manager:</span>{' '}
                        <span className="font-medium text-gray-800">
                          {row.reportingManager || '—'}
                        </span>
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {canViewBehaviour ? (
                        <button
                          type="button"
                          onClick={() => setViewRow(row)}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="View behaviour detail"
                          title="View"
                        >
                          <FaEye className="h-5 w-5" />
                        </button>
                      ) : null}
                      {canEditBehaviour && row.trackingId != null ? (
                        <Link
                          to={editTrackingPath(row)}
                          state={editTrackingState(row)}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="Edit behaviour tracking"
                          title="Edit"
                        >
                          <MdEdit className="h-5 w-5" />
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          {!showEmpty ? (
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Sr. No.
                    </th>
                    {isManagerView ? (
                      <>
                        <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                          Employee Name
                        </th>
                        <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                          Employee ID
                        </th>
                        <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                          Designation
                        </th>
                        <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                          Department
                        </th>
                      </>
                    ) : null}
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Tracking Date
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Reporting Manager
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Review Month
                    </th>
                    {hasRowActions ? (
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Action
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  <ApiLoadTableRow
                    colSpan={colSpan}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    hasData={pageItems.length > 0}
                    error={error}
                    refetch={refetch}
                    message="Loading behaviour data..."
                  />
                  {showData
                    ? pageItems.map((row, index) => (
                        <tr key={row.__key} className="hover:bg-amber-50/40">
                          <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>
                          {isManagerView ? (
                            <>
                              <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                {row.employeeName || '—'}
                              </td>
                              <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                {row.employeeCode || '—'}
                              </td>
                              <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                {row.designation || '—'}
                              </td>
                              <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                {row.department || '—'}
                              </td>
                            </>
                          ) : null}
                          <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                            {row.trackingDateLabel}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                            {row.reportingManager || '—'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                            {row.reviewMonth}
                          </td>
                          {hasRowActions ? (
                            <td className="border-b border-gray-100 px-3 py-2.5">
                              <div className="flex items-center gap-1">
                                {canViewBehaviour ? (
                                  <button
                                    type="button"
                                    onClick={() => setViewRow(row)}
                                    className="rounded-md p-1 text-red-700 hover:bg-amber-50/60"
                                    aria-label="View behaviour detail"
                                    title="View"
                                  >
                                    <FaEye className="h-5 w-5 text-red-700" />
                                  </button>
                                ) : null}
                                {canEditBehaviour && row.trackingId != null ? (
                                  <Link
                                    to={editTrackingPath(row)}
                                    state={editTrackingState(row)}
                                    className="rounded-md p-1 text-red-700 hover:bg-amber-50/60"
                                    aria-label="Edit behaviour tracking"
                                    title="Edit"
                                  >
                                    <MdEdit className="h-5 w-5 text-red-700" />
                                  </Link>
                                ) : null}
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {totalCount > effectivePageSize ? (
            <div className="mt-4 grid grid-cols-1 items-center gap-2 sm:grid-cols-3">
              <div className="hidden sm:block" aria-hidden />
              <div className="flex justify-center [&>div]:mt-0">
                <TablePagination
                  totalCount={totalCount}
                  currentPage={currentPage}
                  onPageChange={setPage}
                  pageSize={effectivePageSize}
                />
              </div>
              <div className="hidden sm:block" aria-hidden />
            </div>
          ) : null}
        </div>
      </div>

      <BehaviourPerformanceModal
        open={Boolean(performanceTarget) || isOverallPerformanceOpen}
        performance={performanceTarget ? rowPerformance : listPerformance}
        subtitle={
          performanceTarget?.name
            ? `${performanceTarget.name}${
                performanceTarget.code ? ` (${performanceTarget.code})` : ''
              }`
            : ''
        }
        isLoading={
          Boolean(performanceTarget) &&
          Boolean(performanceQuery) &&
          (isPerformanceLoading || isPerformanceFetching)
        }
        onClose={() => {
          setPerformanceTarget(null);
          setIsOverallPerformanceOpen(false);
        }}
      />

      {viewRow ? (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-black/30"
          onClick={() => setViewRow(null)}
          role="presentation"
        >
          <div
            className="flex h-full w-full min-w-0 max-w-full flex-col bg-white shadow-2xl sm:max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
              <div>
                <h2 className="text-lg font-semibold text-brand">Behaviour Detail</h2>
                {isManagerView && viewRow.employeeName ? (
                  <p className="mt-0.5 text-sm text-gray-600">
                    {viewRow.employeeName}
                    {viewRow.employeeCode ? ` (${viewRow.employeeCode})` : ''}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                onClick={() => setViewRow(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 pb-10 sm:px-6 sm:pb-8">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Tracking Date</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewRow.trackingDateLabel}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Review Month</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewRow.reviewMonth}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Reporting Manager</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewRow.reportingManager || '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Reviewed By</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewRow.reviewedBy || '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Compliance Rate</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {formatPercent(
                      viewRow.monthlyPerformance ?? viewRow.complianceSummary?.complianceRate
                    )}
                  </p>
                </div>
              </div>

              {viewRow.behaviours.length ? (
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="min-w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#faf8f5]">
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Code</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">
                          Occurrences
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">
                          Remarks
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewRow.behaviours.map((item, idx) => (
                        <tr key={item.id ?? `${item.behaviour_id ?? item.behaviour_name}-${idx}`}>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {firstText(item.behaviour_name, item.code, item.parameter) || '—'}
                          </td>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {formatComplianceStatus(item)}
                          </td>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {getNonComplianceCount(item) ?? '—'}
                          </td>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {firstText(item.manager_remarks, item.remarks, item.remark) || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-gray-500">No behaviour details found.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default AdminBehaviourTracking;
